import {
  AiwynPaymentMethod,
  BankPaymentMethod,
  BankPaymentMethodType,
  BillingDetails,
  CardFunding,
  CardPaymentMethod,
  PaymentMethodBrand,
  PaymentMethodSubType,
  PaymentMethodType,
  PaymentMethodValidationError,
  PaymentMethodValidationErrors,
  PaymentPlatform,
  PaymentPlatformPaymentMethod,
} from '@aiwyn/api';
import { ConfigService, ERROR_EXCLUDE_DEBIT_AND_PREPAID_PROCESSING } from '@aiwyn/config';
import { PaymentMethod } from '@stripe/stripe-js';
import {
  areStringsEqualIgnoreCase,
  CountryAlpha2Code,
  JoinPipe,
  normalizeCountryAlpha2Code,
  UsState,
  VariableTitleCasePipe,
} from '@aiwyn/shared';

import { PAYMENT_METHOD_BRAND_DISPLAY } from './payment-method-metadata.models';

export const PAYMENT_METHOD_ICON_DIRECTORY = 'assets/payment-methods/';
export const AIWYN_PAYMENT_METHOD_VALIDATION_ERROR_MESSAGE = 'Payment method failed Aiwyn specific validation.';

// Inline duplicate-validation message (T3-3893). `noun` is the method category word — "card" for
// cards/wallets, "bank account" for ACH / US bank — matching the recovery dialog's wording.
export const duplicatePaymentMethodMessage = (noun: string): string =>
  `We already have this ${noun} saved. Please check your saved payment methods.`;

const PAYMENT_METHOD_VALIDATION_ERROR_AMEX_DEBIT_PREPAID = `Sorry, we do not accept ${
  JoinPipe.staticTransform(['American Express', 'debit cards', 'prepaid cards'], 'or')
}. Please use a different payment method.`;

export type PlatformCardPaymentMethod = StripeCard; // | OtherCardPaymentMethod;
export type PlatformBankPaymentMethod = StripeBank; // | OtherBankPaymentMethod;

export type StripeTypeToClassMap = {
  [PaymentMethodType.CARD]: StripeCard;
  [PaymentMethodType.GOOGLE_PAY]: StripeCard;
  [PaymentMethodType.APPLE_PAY]: StripeCard;

  [PaymentMethodType.ACH]: StripeBank;
  [PaymentMethodType.ACSS]: StripeBank;
  [PaymentMethodType.US_BANK_ACCOUNT]: StripeBank;
  [PaymentMethodType.ACSS_DEBIT]: StripeBank;
  [PaymentMethodType.BACS]: StripeBank;
  [PaymentMethodType.BACS_DEBIT]: StripeBank;
};

export type StripeTypeToClass<Type extends PaymentMethodType> = StripeTypeToClassMap[Type];

export type AbstractTypeToClass<Platform extends PaymentPlatform, Type extends PaymentMethodType> = Platform extends PaymentPlatform.STRIPE
  ? StripeTypeToClass<Type>
  : never; // | OtherPlatformTypeToClassMap[Platform][Type];

export abstract class AbstractPaymentMethod {
  protected _model: AiwynPaymentMethod;
  protected _defaultPaymentMethodId: string;

  abstract get model(): AiwynPaymentMethod;

  abstract get platform(): PaymentPlatform;

  abstract get country(): CountryAlpha2Code;

  constructor(model: AiwynPaymentMethod, defaultPaymentMethodId: string) {
    this._model = model;
    this._defaultPaymentMethodId = defaultPaymentMethodId;
  }

  /**
   * Creates a new payment method from the given model.
   * @param model
   * @param defaultPaymentMethodId
   */
  static create(model: AiwynPaymentMethod, defaultPaymentMethodId: string): AbstractPaymentMethod {
    if (model == undefined) {
      return undefined;
    }

    return StripePaymentMethod.create(model, defaultPaymentMethodId);
  }

  static marshal(abstractPaymentMethod: PaymentPlatformPaymentMethod): AiwynPaymentMethod {
    if (abstractPaymentMethod == undefined) {
      return undefined;
    }

    return StripePaymentMethod.marshal(abstractPaymentMethod);
  }

  abstract isType<Type extends PaymentMethodType>(type: Type): this is AbstractTypeToClass<PaymentPlatform, Type>;

  // properties
  abstract get id(): string;
  abstract get brand(): string;
  abstract get icon(): string;
  abstract get name(): string;
  abstract get lastFour(): string;

  abstract get type(): PaymentMethodType;
  abstract get subType(): PaymentMethodSubType;
  abstract get isCreditCard(): boolean;

  abstract get billingDetails(): BillingDetails;
  abstract get billingDetailsIncomplete(): boolean;

  abstract get isValid(): boolean;
  abstract get invalidMessage(): string;

  get isDefault(): boolean {
    return this.id === this._defaultPaymentMethodId;
  }

  /**
   * True when an ACH/US bank account was linked through Stripe Financial Connections (the
   * connected-account / instant-verification flow) rather than entered manually. Undefined for
   * cards and for non-ACH bank methods (ACSS/BACS), where the concept doesn't apply.
   */
  get isConnectedAccount(): boolean | undefined {
    return 'isConnectedAccount' in this._model ? this._model.isConnectedAccount : undefined;
  }

  get sortKey(): string {
    return `${this.type === PaymentMethodType.CARD ? '1_' : '2_'}${this.brand}_${this.lastFour}`;
  }

  // methods
  abstract validate(
    configService: ConfigService,
    restrictedUsStates: UsState[],
    savedPaymentMethods?: AbstractPaymentMethod[],
  ): PaymentMethodValidationErrors;

  abstract isDuplicate(savedPaymentMethods: AbstractPaymentMethod[]): boolean;

  abstract equals(paymentMethod: AbstractPaymentMethod): boolean;

  static isDirectDebit(paymentMethodType: PaymentMethodType): boolean {
    const formattedPaymentMethodType = paymentMethodType.toLowerCase();
    return [
      PaymentMethodType.ACH,
      PaymentMethodType.ACSS,
      PaymentMethodType.US_BANK_ACCOUNT,
      PaymentMethodType.ACSS_DEBIT,
      PaymentMethodType.BACS,
    ].map(type => type.toLowerCase()).includes(formattedPaymentMethodType);
  }
}

export abstract class StripePaymentMethod extends AbstractPaymentMethod {
  get model(): AiwynPaymentMethod {
    return this._model;
  }

  get platform(): PaymentPlatform {
    return PaymentPlatform.STRIPE;
  }

  static create(model: AiwynPaymentMethod, defaultPaymentMethodId: string): StripePaymentMethod {
    switch (model.paymentMethodType.toLowerCase()) {
      case BankPaymentMethodType.ACSS_DEBIT:
      case BankPaymentMethodType.BACS_DEBIT:
      case BankPaymentMethodType.US_BANK_ACCOUNT:
      case BankPaymentMethodType.ACSS:
      case BankPaymentMethodType.BACS:
      case BankPaymentMethodType.ACH:
        // TODO: We need to consolidate ^these 6 into 3^. Can we get the raw values from stripe when getting saved payment methods?
        return new StripeBank(model, defaultPaymentMethodId);
      case CardFunding.CREDIT:
      case CardFunding.DEBIT:
      case CardFunding.PREPAID:
      case CardFunding.UNKNOWN:
        return new StripeCard(model, defaultPaymentMethodId);
      default:
        return undefined;
    }
  }

  static marshal(stripePaymentMethod: PaymentMethod): AiwynPaymentMethod {
    if (stripePaymentMethod == undefined) {
      return undefined;
    }

    return stripePaymentMethod.type === PaymentMethodType.CARD
      ? StripeCard.marshal(stripePaymentMethod)
      : StripeBank.marshal(stripePaymentMethod);
  }

  isType<Type extends PaymentMethodType>(type: Type): this is StripeTypeToClass<Type> {
    return this.type === type;
  }

  get id(): string {
    return this._model.id;
  }

  get brand(): string {
    return this._model.brand;
  }

  get lastFour(): string {
    return this._model.last4;
  }

  get billingDetails(): BillingDetails {
    return this._model.billingDetails;
  }
}

export class StripeCard extends StripePaymentMethod {
  protected _model: CardPaymentMethod;
  get model(): CardPaymentMethod {
    return this._model;
  }

  static marshal(stripeCardPaymentMethod: PaymentMethod): AiwynPaymentMethod {
    if (stripeCardPaymentMethod == undefined) {
      return undefined;
    }

    return {
      platform: PaymentPlatform.STRIPE,
      id: stripeCardPaymentMethod.id,
      billingDetails: {
        address: {
          city: stripeCardPaymentMethod.billing_details.address.city,
          country: stripeCardPaymentMethod.billing_details.address.country,
          line1: stripeCardPaymentMethod.billing_details.address.line1,
          line2: stripeCardPaymentMethod.billing_details.address.line2,
          postalCode: stripeCardPaymentMethod.billing_details.address.postal_code,
          state: stripeCardPaymentMethod.billing_details.address.state,
        },
        name: stripeCardPaymentMethod.billing_details.name,
      },
      country: stripeCardPaymentMethod.card.country as CountryAlpha2Code,
      last4: stripeCardPaymentMethod.card.last4,
      name: stripeCardPaymentMethod.billing_details.name,
      brand: stripeCardPaymentMethod.card.brand as PaymentMethodBrand,
      expirationMonth: stripeCardPaymentMethod.card.exp_month,
      expirationYear: stripeCardPaymentMethod.card.exp_year,
      paymentMethodType: stripeCardPaymentMethod.card.funding as CardFunding,
      wallet: stripeCardPaymentMethod.card.wallet?.type as string, // e.g., 'apple_pay', 'google_pay', etc.
    };
  }

  get brand(): PaymentMethodBrand {
    return this._model.brand;
  }

  get icon(): string {
    return `${PAYMENT_METHOD_ICON_DIRECTORY}${this._model.brand.toLowerCase()}.svg`;
  }

  get name(): string {
    return PAYMENT_METHOD_BRAND_DISPLAY[this._model.brand];
  }

  get type(): PaymentMethodType {
    return PaymentMethodType.CARD;
  }

  get subType(): CardFunding {
    return this._model.paymentMethodType;
  }

  get isCreditCard(): boolean {
    return areStringsEqualIgnoreCase(this.subType, CardFunding.CREDIT);
  }

  get wallet(): PaymentMethodType.GOOGLE_PAY | PaymentMethodType.APPLE_PAY {
    return this._model.wallet as PaymentMethodType.GOOGLE_PAY | PaymentMethodType.APPLE_PAY;
  }

  get expirationMonth(): number {
    return this._model.expirationMonth;
  }

  get expirationYear(): number {
    return this._model.expirationYear;
  }

  get billingDetailsIncomplete(): boolean {
    if (this._model.billingDetails == null) {
      return true;
    }

    const addressEntries = Object.entries(this._model.billingDetails.address);

    if (addressEntries.length === 0) {
      return true;
    }

    // Stripe seems to always require Address Line 1 for any country.
    // This doesn't catch invalid data where Line 1 is provided but some other field is empty. The frontend does not allow this.
    const ignoredAddressFields = ['line2', 'state'];
    return Object.entries(this._model.billingDetails.address)
      .filter(([key]) => !ignoredAddressFields.includes(key)).some(([_, value]) => !value?.trim());
  }

  get isValid(): boolean {
    return Object.keys(this._model.validationErrors ?? {}).length === 0;
  }

  get invalidMessage(): string {
    if (this.isValid) {
      return undefined;
    }

    return (this._model.validationErrors ?? {}).expiration ? 'Card expired' : 'No Longer accepted';
  }

  get country(): CountryAlpha2Code {
    return (this._model.country?.trim() as CountryAlpha2Code) || CountryAlpha2Code.UNITED_STATES;
  }

  validate(
    configService: ConfigService,
    restrictedUsStates: UsState[],
    savedPaymentMethods: AbstractPaymentMethod[] = [],
  ): PaymentMethodValidationErrors {
    const errors: PaymentMethodValidationErrors = {};

    // FUNDING
    if (
      configService.excludeDebitAndPrepaidProcessing
      && (
        areStringsEqualIgnoreCase(this._model.paymentMethodType, CardFunding.DEBIT)
        || areStringsEqualIgnoreCase(this._model.paymentMethodType, CardFunding.PREPAID)
      )
    ) {
      errors[PaymentMethodValidationError.FUNDING] = configService.excludeAmexProcessingEnabled
        ? PAYMENT_METHOD_VALIDATION_ERROR_AMEX_DEBIT_PREPAID
        : ERROR_EXCLUDE_DEBIT_AND_PREPAID_PROCESSING;
    }

    // BRAND
    if (configService.excludeAmexProcessingEnabled && this._model.brand === PaymentMethodBrand.AMEX) {
      errors[PaymentMethodValidationError.BRAND] = configService.excludeDebitAndPrepaidProcessing
        ? PAYMENT_METHOD_VALIDATION_ERROR_AMEX_DEBIT_PREPAID
        : 'Sorry, we do not accept American Express. Please use a different payment method.';
    }

    // EXPIRATION
    const today = new Date();
    today.setTime(0);
    const expirationDate = new Date(this._model.expirationYear, this._model.expirationMonth - 1);
    if (expirationDate < today) {
      errors[PaymentMethodValidationError.EXPIRATION] = 'Card expired';
    }

    // COUNTRY
    if (configService.usPaymentsOnly && normalizeCountryAlpha2Code(this.country) !== CountryAlpha2Code.UNITED_STATES) {
      errors[PaymentMethodValidationError.COUNTRY] = 'Only US cards are accepted.';
    }

    // STATE
    const billingState = this._model.billingDetails.address.state;
    const restrictedState = restrictedUsStates.find(usState => usState.stateAbbreviation === this._model.billingDetails.address.state);
    if (restrictedState) {
      errors[PaymentMethodValidationError.STATE] = `Sorry, we do not accept credit cards from ${billingState}. Please use a different payment method.`;
    }

    // DUPLICATE
    savedPaymentMethods = savedPaymentMethods.filter(({ id }) => id !== this.id);
    if (this.isDuplicate(savedPaymentMethods)) {
      errors[PaymentMethodValidationError.DUPLICATE] = duplicatePaymentMethodMessage('card');
    }

    // INCOMPLETE BILLING DETAILS
    if (this.billingDetailsIncomplete && this._model.wallet) {
      const walletName = this.walletName();
      errors[PaymentMethodValidationError.INCOMPLETE_BILLING_DETAILS] = `The complete billing address is required to save this payment method. Please fill your billing address in ${walletName}`;
    }

    return Object.keys(errors).length === 0 ? undefined : errors;
  }

  private walletName(): string {
    switch (this._model.wallet) {
      case 'apple_pay':
        return 'Apple Pay';
      case 'google_pay':
        return  'Google Pay';
      default:
        return 'Digital Wallet';
    }
  }

  isDuplicate(savedPaymentMethods: AbstractPaymentMethod[]): boolean {
    return savedPaymentMethods.some(savedPaymentMethod => this.equals(savedPaymentMethod));
  }

  equals(paymentMethod: AbstractPaymentMethod): boolean {
    return this.type === paymentMethod.type
      && this.subType === paymentMethod.subType
      && this.brand === paymentMethod.brand
      && this.lastFour === paymentMethod.lastFour
      && paymentMethod instanceof StripeCard
      && this.expirationMonth === paymentMethod.expirationMonth
      && this.expirationYear === paymentMethod.expirationYear;
  }
}

export class StripeBank extends StripePaymentMethod {
  protected _model: BankPaymentMethod;
  get model(): BankPaymentMethod {
    return this._model;
  }

  static marshal(stripeBankPaymentMethod: PaymentMethod): AiwynPaymentMethod {
    if (stripeBankPaymentMethod == undefined) {
      return undefined;
    }

    let paymentMethodType: BankPaymentMethodType;
    let country: CountryAlpha2Code;
    if (stripeBankPaymentMethod.type === PaymentMethodType.US_BANK_ACCOUNT) {
      paymentMethodType = BankPaymentMethodType.ACH;
      country = CountryAlpha2Code.UNITED_STATES;
    }
    if (stripeBankPaymentMethod.type === PaymentMethodType.ACSS_DEBIT) {
      paymentMethodType = BankPaymentMethodType.ACSS;
      country = CountryAlpha2Code.CANADA;
    }
    if (stripeBankPaymentMethod.type === PaymentMethodType.BACS_DEBIT) {
      paymentMethodType = BankPaymentMethodType.BACS;
      country = CountryAlpha2Code.UNITED_KINGDOM;
    }

    return {
      platform: PaymentPlatform.STRIPE,
      id: stripeBankPaymentMethod.id,
      brand: stripeBankPaymentMethod[stripeBankPaymentMethod.type].bank_name,
      billingDetails: {
        address: {
          city: stripeBankPaymentMethod.billing_details.address.city,
          country: stripeBankPaymentMethod.billing_details.address.country,
          line1: stripeBankPaymentMethod.billing_details.address.line1,
          line2: stripeBankPaymentMethod.billing_details.address.line2,
          postalCode: stripeBankPaymentMethod.billing_details.address.postal_code,
          state: stripeBankPaymentMethod.billing_details.address.state,
        },
        email: stripeBankPaymentMethod.billing_details.email,
        name: stripeBankPaymentMethod.billing_details.name,
      },
      country,
      last4: stripeBankPaymentMethod[stripeBankPaymentMethod.type].last4,
      name: stripeBankPaymentMethod.billing_details.name,
      paymentMethodType,
      // Financial Connections populates `financial_connections_account` only when the bank was
      // linked via the connected-account flow; manual routing/account entry leaves it absent. Only
      // meaningful for ACH (us_bank_account) — left undefined for ACSS/BACS.
      isConnectedAccount: stripeBankPaymentMethod.type === PaymentMethodType.US_BANK_ACCOUNT
        ? !!stripeBankPaymentMethod.us_bank_account?.financial_connections_account
        : undefined,
    };
  }

  get brand(): string {
    return this._model.brand;
  }

  get icon(): string {
    return `${PAYMENT_METHOD_ICON_DIRECTORY}stripe-bank.svg`;
  }

  get name(): string {
    return VariableTitleCasePipe.staticTransform(this._model.brand);
  }

  get type(): PaymentMethodType {
    return this._model.paymentMethodType as unknown as PaymentMethodType;
  }

  get subType(): PaymentMethodSubType {
    return undefined;
  }

  get isCreditCard(): boolean {
    return false;
  }

  get billingDetailsIncomplete(): boolean {
    return false;
  }

  get isValid(): boolean {
    return true;
  }

  get invalidMessage(): string {
    return undefined;
  }

  get country(): CountryAlpha2Code {
    if ([PaymentMethodType.ACSS, PaymentMethodType.ACSS_DEBIT].includes(this.type)) {
      return CountryAlpha2Code.CANADA;
    }

    if ([PaymentMethodType.BACS, PaymentMethodType.BACS_DEBIT].includes(this.type)) {
      return normalizeCountryAlpha2Code(this._model.country) || CountryAlpha2Code.UNITED_KINGDOM; // fallback just to be safe
    }

    return CountryAlpha2Code.UNITED_STATES;
  }

  validate(
    _: ConfigService,
    __: UsState[],
    savedPaymentMethods?: AbstractPaymentMethod[],
  ): PaymentMethodValidationErrors {
    return this.isDuplicate(savedPaymentMethods) ? { [PaymentMethodValidationError.DUPLICATE]: duplicatePaymentMethodMessage('bank account') } : undefined;
  }

  isDuplicate(savedPaymentMethods: AbstractPaymentMethod[]): boolean {
    return savedPaymentMethods.some(savedPaymentMethod => this.equals(savedPaymentMethod));
  }

  equals(paymentMethod: AbstractPaymentMethod): boolean {
    return this.type === paymentMethod.type && this.brand === paymentMethod.brand && this.lastFour === paymentMethod.lastFour;
  }
}
